import { action, computed, flow, makeObservable, observable } from 'mobx';

import type { TJob, TImportJobResult } from 'src/api/import-file';
import { getJobList, cancelJob, getJobInfo } from 'src/api/import-file';

import type { PlanVersionsService } from '../plan-version/plan-versions-service';

import { ImportJob } from './import-job';

interface IUserStore {
  userInfo: {
    sub: string;
  } | null;
}

export enum JOB_STATUS {
  NEW = 'NEW',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  ERROR = 'ERROR',
  CANCELLED = 'CANCELLED',
  CANCEL = 'CANCEL',
}
const JOB_SYNC_INTERVAL = 2000;
const COMPLETED_JOB_STATUSES = [JOB_STATUS.CANCELLED, JOB_STATUS.COMPLETED, JOB_STATUS.ERROR];
// ERROR временно отключен
// const ACTIVE_JOB_STATUSES = [JOB_STATUS.NEW, JOB_STATUS.IN_PROGRESS, JOB_STATUS.ERROR, JOB_STATUS.COMPLETED];
const ACTIVE_JOB_STATUSES = [JOB_STATUS.NEW, JOB_STATUS.IN_PROGRESS, JOB_STATUS.COMPLETED];

export interface IJob<T extends object | null = object | null> {
  readonly id: number;

  timerId: number | null;
  status: JOB_STATUS;
  result: T;

  completeJob(jobData: TJob): Promise<void>;
}

export class JobsStore {
  private readonly planVersionsService: PlanVersionsService;
  private readonly userStore: IUserStore;
  readonly activeJobsMap = new Map<number, IJob>();
  readonly jobDisposers: Map<IJob, VoidFunction> = new Map();

  @observable activeJobs: IJob[] = [];

  constructor(planVersionsService: PlanVersionsService, userStore: IUserStore) {
    this.planVersionsService = planVersionsService;
    this.userStore = userStore;

    makeObservable(this);
  }

  /**
   * @deprecated
   * Есть ли для текущей плановой версии соответствующая импорт джоба с драфтом импорта
   */
  @computed
  get currentImportVersionJob(): ImportJob | null {
    if (!this.activeJobs.length) {
      return null;
    }

    const currentVersion = this.planVersionsService.currentPlanVersion;

    if (!currentVersion) {
      console.error('there is no currentVersion');
      return null;
    }

    const currentDraft = this.planVersionsService.currentDraft;

    // Просто дополнительный слой защиты
    if (currentDraft && currentDraft.data.parentVersionId !== currentVersion.id) {
      console.error('current version is not a parent for a current draft');
      return null;
    }

    return (
      this.activeJobs
        .filter((j) => j instanceof ImportJob)
        .find((job) => {
          const jobImportDraft = this.planVersionsService.draftsList.find((d) => d.id === job.result?.planVersionId);

          if (!jobImportDraft) {
            return false;
          }

          return (
            jobImportDraft?.data.parentVersionId === currentDraft?.id ||
            jobImportDraft?.data.parentVersionId === currentVersion?.id
          );
        }) ?? null
    );
  }

  @flow.bound
  private async *loadJobs(): Promise<void> {
    try {
      const rawJobs = await getJobList();
      yield;

      const activeJobsReqs: Promise<IJob | null>[] = [];

      const userSid = this.userStore.userInfo?.sub;

      if (!userSid) {
        console.error('userSid is not presented');
        return;
      }

      rawJobs.forEach(async (job) => {
        if (!ACTIVE_JOB_STATUSES.includes(job.jobStatus)) {
          return;
        }

        if ((job.jobResult as TImportJobResult)?.userSid === userSid) {
          activeJobsReqs.push(this.registerJob(job));
        }
      });

      const activeJobs = (await Promise.all(activeJobsReqs)).filter((job) => !!job);
      yield;

      this.activeJobs.forEach((j) => this.jobDisposers.get(j)?.());
      this.activeJobsMap.clear();
      this.jobDisposers.clear();

      activeJobs.forEach((job) => {
        const disposeJob = this.initJob(job);
        this.jobDisposers.set(job, disposeJob);
        this.activeJobsMap.set(job.id, job);
      });

      this.activeJobs = activeJobs;
    } catch (error) {
      yield;
      console.error(error);
    }
  }

  @flow.bound
  async *addJob(rawJob: TJob) {
    if (!ACTIVE_JOB_STATUSES.includes(rawJob.jobStatus)) {
      return;
    }

    const userSid = this.userStore.userInfo?.sub;

    if (!userSid) {
      console.error('userSid is not presented');
      return;
    }

    if ((rawJob.jobResult as TImportJobResult)?.userSid !== userSid) {
      return;
    }

    const jobInstance = await this.registerJob(rawJob);
    yield;

    if (!jobInstance) {
      console.error('job was not registered');
      return;
    }

    const disposeJob = this.initJob(jobInstance);
    this.jobDisposers.set(jobInstance, disposeJob);
    this.activeJobsMap.set(jobInstance.id, jobInstance);
    this.activeJobs = [...this.activeJobs, jobInstance];
  }

  @action.bound
  private stopJobSync(job: IJob): void {
    if (job.timerId !== null) {
      window.clearInterval(job.timerId);
      job.timerId = null;
    }
  }

  @action.bound
  private initJob(job: IJob): VoidFunction {
    this.stopJobSync(job);

    job.timerId = window.setInterval(() => this.syncJob(job), JOB_SYNC_INTERVAL);

    return () => {
      this.stopJobSync(job);
    };
  }

  @flow.bound
  async *syncJob(job: IJob) {
    const jobData = await getJobInfo(job.id);
    yield;

    if (COMPLETED_JOB_STATUSES.includes(job.status)) {
      this.stopJobSync(job);
    }

    await job.completeJob(jobData);
  }

  @action.bound
  private async createJob(job: TJob): Promise<IJob | null> {
    try {
      switch (job.jobType) {
        case 'IMPORT_FROM_EXCEL': {
          const importJob = new ImportJob(job as TJob<TImportJobResult>);

          await importJob.completeJob(job as TJob<TImportJobResult>);

          return importJob;
        }

        default: {
          return null;
        }
      }
    } catch (error) {
      console.error(error);
    }

    return null;
  }

  @flow.bound
  private async *registerJob(rawJob: TJob): Promise<IJob | null> {
    try {
      const job = await this.createJob(rawJob);
      yield;

      return job;
    } catch (error) {
      console.error(error);
    }

    return null;
  }

  @flow.bound
  async *cancelJob(jobId: number) {
    const job = this.activeJobsMap.get(jobId);

    if (!job) {
      return;
    }

    try {
      await cancelJob(job.id);
      yield;

      this.stopJobSync(job);
      const jobDisposer = this.jobDisposers.get(job);
      jobDisposer?.();

      this.activeJobsMap.delete(job.id);
      const copiedActiveJobs = [...this.activeJobs];

      const jobIndex = copiedActiveJobs.indexOf(job);

      if (jobIndex < 0) {
        return;
      }

      copiedActiveJobs.splice(jobIndex, 1);

      this.activeJobs = copiedActiveJobs;
    } catch (error) {
      yield;
      console.error(error);
    }
  }

  init = async (): Promise<void> => {
    await this.loadJobs();
  };
}
